/**
 * canonical-product-loader.js
 * ============================================================
 * Drop this file into your Shopify theme's /assets/ folder.
 *
 * PURPOSE:
 *   Replaces the Liquid `all_products[]` lookups that were
 *   causing the 20-product cap to be hit and nuking the product
 *   variable. Fetches the canonical (main) product and any
 *   other_products via Shopify's /products/[handle].js endpoint,
 *   then merges variants/images and fires a custom event so the
 *   theme can re-render selectors.
 *
 *   Also fires canonical:loaded on products with no splits so
 *   the turbo hook (size highlight, badge fix etc.) runs on
 *   every product page.
 *
 * USAGE:
 *   Add to your product template Liquid file:
 *   {{ 'canonical-product-loader.js' | asset_url | script_tag }}
 * ============================================================
 */

(function () {
  'use strict';

  // ── Sanitize handles from API (fixes triple-dash bug) ─────
  // The FTP sync generates handles with triple dashes (e.g. gildan---heavy).
  // Shopify product handles only ever use single dashes, so we collapse
  // any run of 2+ dashes down to a single dash before fetching.
  function sanitizeHandle(handle) {
    if (!handle) return handle;
    return handle.trim().replace(/-{2,}/g, '-');
  }

  // ── Config injected by Liquid ──────────────────────────────
  var config = window.canonicalProductConfig;

  if (!config) {
    console.warn('[CanonicalLoader] window.canonicalProductConfig not found. Did you add the Liquid config block?');
    return;
  }

  var currentHandle       = sanitizeHandle(config.currentHandle);
  var mainProductHandle   = sanitizeHandle(config.mainProductHandle)   || null;
  var otherProductHandles = config.otherProductHandles || '';

  // Parse the comma-separated other_products string into an array
  var otherHandles = otherProductHandles
    ? otherProductHandles.split(',').map(function (h) { return sanitizeHandle(h); }).filter(Boolean)
    : [];

  // ── Fire the canonical:loaded event ───────────────────────
  function fireEvent(variants, images) {
    // Update global variant stores so changesizesstatus and
    // other Turbo JS reads the merged data, not just the current page's
    window.productVariants     = variants;
    window.productVariantsJson = variants;
    window.productJson         = window.productJson || {};
    window.productJson.variants = variants;

    window.canonicalLoaderResult = { variants: variants, images: images };

    var event = new CustomEvent('canonical:loaded', {
      bubbles: true,
      detail: {
        variants: variants,
        images  : images
      }
    });
    document.dispatchEvent(event);
  }

  // ── Fetch a single product by handle ──────────────────────
  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) {
          console.warn('[CanonicalLoader] Could not fetch product: ' + handle + ' (status ' + response.status + ')');
          return null;
        }
        return response.json();
      })
      .catch(function (err) {
        console.warn('[CanonicalLoader] Fetch error for handle: ' + handle, err);
        return null;
      });
  }

  // ── Main loader ────────────────────────────────────────────
  function loadCanonicalProduct() {

    var needsMainFetch = mainProductHandle && mainProductHandle !== currentHandle;

    // Nothing to merge — fire event with current product's data
    // so the turbo hook still runs (size highlight, badge fix, etc.)
    if (!needsMainFetch && otherHandles.length === 0) {
      console.log('[CanonicalLoader] No external products to load. Firing event with current variants.');
      var currentVariants = Array.isArray(window.productVariants) ? window.productVariants : [];
      var currentImages   = Array.isArray(window.productImages)   ? window.productImages   : [];
      fireEvent(currentVariants, currentImages);
      return;
    }

    // Build fetch promises
    var fetchPromises = [];
    if (needsMainFetch) {
      fetchPromises.push(fetchProduct(mainProductHandle));
    } else {
      fetchPromises.push(Promise.resolve(null));
    }
    otherHandles.forEach(function (handle) {
      fetchPromises.push(fetchProduct(handle));
    });

    Promise.all(fetchPromises).then(function (results) {
      var mainProduct  = results[0];
      var otherResults = results.slice(1);

      // Start from current page's seeded data
      var mergedVariants = Array.isArray(window.productVariants) ? window.productVariants.slice() : [];
      var mergedImages   = Array.isArray(window.productImages)   ? window.productImages.slice()   : [];

      // Swap in canonical main product if fetched
      if (mainProduct) {
        mergedVariants = mainProduct.variants || [];
        mergedImages   = mainProduct.images   || [];
        console.log('[CanonicalLoader] Canonical main product loaded: ' + mainProduct.handle);
      }

      // Merge in other_products
      otherResults.forEach(function (otherProduct, i) {
        if (!otherProduct) {
          console.warn('[CanonicalLoader] Skipping null result for other_products handle: ' + (otherHandles[i] || 'unknown'));
          return;
        }
        if (otherProduct.variants) {
          mergedVariants = mergedVariants.concat(otherProduct.variants);
        }
        if (otherProduct.images) {
          mergedImages = mergedImages.concat(otherProduct.images);
        }
        console.log('[CanonicalLoader] Merged other product: ' + otherProduct.handle);
      });

      // Deduplicate by id
      mergedVariants = deduplicateById(mergedVariants);
      mergedImages   = deduplicateById(mergedImages);

      console.log(
        '[CanonicalLoader] Done. Variants: ' + mergedVariants.length +
        ', Images: ' + mergedImages.length
      );

      fireEvent(mergedVariants, mergedImages);

    }).catch(function (err) {
      console.error('[CanonicalLoader] Unexpected error during product loading:', err);
    });
  }

  // ── Utility: deduplicate an array of objects by .id ───────
  function deduplicateById(arr) {
    var seen = {};
    return arr.filter(function (item) {
      if (!item || !item.id) return true;
      if (seen[item.id]) return false;
      seen[item.id] = true;
      return true;
    });
  }

  // ── Kick off on DOMContentLoaded ──────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadCanonicalProduct);
  } else {
    loadCanonicalProduct();
  }

})();